import './tableData.scss'
import {useEffect, useState, useMemo} from "react";
import {
    Table,
    Thead,
    Tbody,
    Tfoot,
    Tr,
    Th,
    Td,
    TableCaption,
    TableContainer,
} from '@chakra-ui/react'
import {formatOra} from "../../utilities/formatDate";
import {formatStrategyName} from "../../utilities/formatStrategyName";

const SortIndicator = ({ columnKey, sortConfig }) => {
    if (sortConfig.key !== columnKey) return <span style={{marginLeft: '4px', fontSize: '10px'}}>↕</span>;
    return <span style={{marginLeft: '4px', fontSize: '10px'}}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>;
};

const thStyle = { cursor: 'pointer', userSelect: 'none' };

const TableData = ({actualStep, data, filter}) => {
    const [filteredData, setFilteredData] = useState(data);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    // Funzione per parsare i log del primo tab (Security)
    const parseSecurityLog = (msg) => {
        if (!msg) return null;

        // Formato: "NOG | open: 22.62 | previousClose: 21.8 | gap percentage: 0.03761467889908258"
        const parts = msg.split('|').map(p => p.trim());

        if (parts.length < 4) return null;

        const security = parts[0];
        const open = parts[1]?.replace('open:', '').trim();
        const previousClose = parts[2]?.replace('previousClose:', '').trim();
        const gapRaw = parts[3]?.replace('gap percentage:', '').trim();
        const gap = gapRaw ? (parseFloat(gapRaw) * 100).toFixed(2) + '%' : '-';

        return { security, open, previousClose, gap };
    };

    useEffect(() => {
        if (!data && !filter && !actualStep) return;

        let processedData = [...data]; // Copia array per non mutare l'originale

        // Ordina per timestamp decrescente (più recenti in cima)
        processedData.sort((a, b) => {
            const dateA = new Date(a.ts_event_iso);
            const dateB = new Date(b.ts_event_iso);
            return dateB - dateA; // Ordine decrescente
        });

        // Applica filtro se presente
        if (!filter) {
            setFilteredData(processedData);
            return;
        }

        if (actualStep !== 0) {
            setFilteredData(processedData.filter(row =>
                row.security.toLowerCase() === filter.toLowerCase()
            ));
        } else {
            setFilteredData(processedData.filter(row =>
                row.msg.toLowerCase().includes(filter.toLowerCase())
            ));
        }
    }, [actualStep, data, filter]);

    const sortedData = useMemo(() => {
        if (!sortConfig.key || !filteredData) return filteredData;

        return [...filteredData].sort((a, b) => {
            let valA, valB;

            if (actualStep === 0) {
                const parsedA = parseSecurityLog(a.msg);
                const parsedB = parseSecurityLog(b.msg);
                switch (sortConfig.key) {
                    case 'security':
                        valA = parsedA?.security?.toLowerCase() || '';
                        valB = parsedB?.security?.toLowerCase() || '';
                        break;
                    case 'open':
                        valA = parseFloat(parsedA?.open) || 0;
                        valB = parseFloat(parsedB?.open) || 0;
                        break;
                    case 'previousClose':
                        valA = parseFloat(parsedA?.previousClose) || 0;
                        valB = parseFloat(parsedB?.previousClose) || 0;
                        break;
                    case 'gap':
                        valA = parseFloat(parsedA?.gap) || 0;
                        valB = parseFloat(parsedB?.gap) || 0;
                        break;
                    case 'ora':
                        valA = new Date(a.ts_event_iso);
                        valB = new Date(b.ts_event_iso);
                        break;
                    default:
                        return 0;
                }
            } else {
                switch (sortConfig.key) {
                    case 'security':
                        valA = (a.security || '').toLowerCase();
                        valB = (b.security || '').toLowerCase();
                        break;
                    case 'msg':
                        valA = (a.msg || '').toLowerCase();
                        valB = (b.msg || '').toLowerCase();
                        break;
                    case 'ora':
                        valA = new Date(a.ts_event_iso);
                        valB = new Date(b.ts_event_iso);
                        break;
                    default:
                        return 0;
                }
            }

            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortConfig, actualStep]);

    return (
        <TableContainer
            maxH="calc(100vh - 350px)"
            overflowY="auto"
            sx={{
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: '#555',
                },
            }}
        >
            <Table variant='simple' className="table">
                {actualStep === 0 && <TableCaption>Selected Securities by Gap Percentage</TableCaption>}
                {actualStep === 1 && <TableCaption>Securities Passed First Bar</TableCaption>}
                {actualStep === 2 && <TableCaption>Securities Passed Second Bar</TableCaption>}
                {actualStep === 3 && <TableCaption>Let the trade Begin </TableCaption>}

                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                        {actualStep === 0 ? (
                            // Header per il primo tab (Security)
                            <>
                                <Th style={thStyle} onClick={() => handleSort('security')}>
                                    Security<SortIndicator columnKey="security" sortConfig={sortConfig} />
                                </Th>
                                <Th style={thStyle} onClick={() => handleSort('open')}>
                                    Open<SortIndicator columnKey="open" sortConfig={sortConfig} />
                                </Th>
                                <Th style={thStyle} onClick={() => handleSort('previousClose')}>
                                    Previous Close<SortIndicator columnKey="previousClose" sortConfig={sortConfig} />
                                </Th>
                                <Th style={thStyle} onClick={() => handleSort('gap')}>
                                    Gap<SortIndicator columnKey="gap" sortConfig={sortConfig} />
                                </Th>
                                <Th style={thStyle} onClick={() => handleSort('ora')}>
                                    Ora<SortIndicator columnKey="ora" sortConfig={sortConfig} />
                                </Th>
                            </>
                        ) : (
                            // Header per gli altri tab
                            <>
                                {data && data[0]['security'] ? (
                                    <Th style={thStyle} onClick={() => handleSort('security')}>
                                        Security<SortIndicator columnKey="security" sortConfig={sortConfig} />
                                    </Th>
                                ) : ''}
                                <Th style={thStyle} onClick={() => handleSort('msg')}>
                                    Logs<SortIndicator columnKey="msg" sortConfig={sortConfig} />
                                </Th>
                                <Th style={thStyle} onClick={() => handleSort('ora')}>
                                    Ora<SortIndicator columnKey="ora" sortConfig={sortConfig} />
                                </Th>
                            </>
                        )}
                    </Tr>
                </Thead>
                <Tbody>
                    {sortedData?.map((row, index) => {
                        if (actualStep === 0) {
                            // Parsing per il primo tab
                            const parsed = parseSecurityLog(row.msg);
                            if (!parsed) {
                                // Fallback se il parsing fallisce
                                return (
                                    <Tr key={index}>
                                        <Td colSpan={5} whiteSpace="normal" wordBreak="break-word">{row.msg}</Td>
                                    </Tr>
                                );
                            }
                            return (
                                <Tr key={index}>
                                    <Td>{parsed.security}</Td>
                                    <Td>{parsed.open}</Td>
                                    <Td>{parsed.previousClose}</Td>
                                    <Td>{parsed.gap}</Td>
                                    <Td>{formatOra(row.ts_event_iso)}</Td>
                                </Tr>
                            );
                        } else {
                            // Visualizzazione normale per gli altri tab
                            return (
                                <Tr key={index}>
                                    {row?.security ? (
                                        <Td>{row.security}</Td>
                                    ) : ''}
                                    <Td whiteSpace="normal" wordBreak="break-word">{row.msg}</Td>
                                    <Td>{formatOra(row.ts_event_iso)}</Td>
                                </Tr>
                            );
                        }
                    })}
                </Tbody>
            </Table>
        </TableContainer>
    );
}

export default TableData;